import { Alert, Grid, Section, SnackbarManager } from '@cmg/design-system';
import { FormikProvider, useFormik } from 'formik';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import { useCallback, useMemo, useState } from 'react';

import { DescriptionList } from '../../../../../../design-system/body-sections/description-list/DescriptionList';
import { DescriptionListItem } from '../../../../../../design-system/body-sections/description-list/DescriptionListItem';
import { PageGridSectionActions } from '../../../../../../design-system/body-sections/PageGridSectionActions';
import type { UpdateInvestorFirmInput } from '../../../../../../graphql';
import { SECTIONS_SIZING } from '../../../crm-sales-coverage/employee-detail/models/UserDetailsSection.model';
import type { InvestorFirmFragment } from '../../graphql/__generated__';
import { useUpdateInvestorFirm } from '../hooks/useUpdateInvestorFirm';
import {
  getInvestorFirmDetailInitialValues,
  INVESTOR_FIRM_DETAIL_FIELD_LABELS,
} from '../models/InvestorFirmDetail.model';
import { ContactInformationFields } from './ContactInformationFields';

export type ContactInformationSectionProps = Readonly<{
  tenantId?: string;
  firm: InvestorFirmFragment;
}>;

export function ContactInformationSection({ tenantId, firm }: ContactInformationSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const initialValues = useMemo(() => getInvestorFirmDetailInitialValues(firm), [firm]);
  const { updateInvestorFirm, error: updateInvestorFirmContactInfoError } =
    useUpdateInvestorFirm(tenantId);

  const handleSubmit = useCallback(
    async (values: UpdateInvestorFirmInput) => {
      try {
        const contactInformation = pick(values, [
          'website1',
          'website2',
          'address1',
          'address2',
          'phoneNumber1',
          'phoneNumber2',
          'email1',
          'email2',
          'faxNumber1',
          'faxNumber2',
        ]);

        const firmKeyOrId = firm.firmKey ?? firm.id;

        if (isNil(firmKeyOrId)) {
          throw new Error('Firm Key or ID is required');
        }
        await updateInvestorFirm(firmKeyOrId, {
          ...contactInformation,
          name: firm.name,
          investorType: firm.firmType,
          investorSubType: firm.firmSubType,
          cik1: firm.cik1,
          cik2: firm.cik2,
          lei: firm.lei,
          factSet: firm.factSet,
          description: firm.description,
          status: firm.status,
        });
        setIsEditing(false);
        SnackbarManager.success('Contact Information updated successfully.');
      } catch {
        SnackbarManager.error('An error occurred while updating contact information.');
      }
    },
    [updateInvestorFirm, firm]
  );

  const formik = useFormik<UpdateInvestorFirmInput>({
    initialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    formik.resetForm();
  }, [formik]);

  const handleSave = useCallback(() => {
    formik.handleSubmit();
  }, [formik]);

  return (
    <FormikProvider value={formik}>
      <Grid item {...SECTIONS_SIZING}>
        <Section
          title="Contact Information"
          data-testid="contact-information-section"
          actions={[
            <PageGridSectionActions
              key="page-grid-section-actions"
              isSubmitting={formik.isSubmitting}
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onCancel={handleCancel}
              onSave={handleSave}
              canEdit
            />,
          ]}
        >
          {updateInvestorFirmContactInfoError && (
            <Alert severity="error">{updateInvestorFirmContactInfoError.message}</Alert>
          )}
          {isEditing ? (
            <ContactInformationFields />
          ) : (
            <DescriptionList>
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.website1}
                value={defaultTo(firm.website1, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.website2}
                value={defaultTo(firm.website2, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.address1}
                value={defaultTo(firm.address1, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.address2}
                value={defaultTo(firm.address2, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.email1}
                value={defaultTo(firm.emailAddress1, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.email2}
                value={defaultTo(firm.emailAddress2, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.phoneNumber1}
                value={defaultTo(firm.phone1, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.phoneNumber2}
                value={defaultTo(firm.phone2, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.faxNumber1}
                value={defaultTo(firm.fax1, '-')}
              />
              <DescriptionListItem
                label={INVESTOR_FIRM_DETAIL_FIELD_LABELS.faxNumber2}
                value={defaultTo(firm.fax2, '-')}
              />
            </DescriptionList>
          )}
        </Section>
      </Grid>
    </FormikProvider>
  );
}
